/**
 * Elixir's `update_in` and `put_in` for immutable values: `path` names the
 * keys from the root, and only the objects along it are copied. Paths go up to
 * five keys; each key must exist on the type at its position, so a union must
 * be narrowed before a path goes through it.
 */
export function updateIn<T, K1 extends keyof T>(
  value: T,
  path: readonly [K1],
  update: (current: T[K1]) => T[K1]
): T
export function updateIn<T, K1 extends keyof T, K2 extends keyof T[K1]>(
  value: T,
  path: readonly [K1, K2],
  update: (current: T[K1][K2]) => T[K1][K2]
): T
export function updateIn<T, K1 extends keyof T, K2 extends keyof T[K1], K3 extends keyof T[K1][K2]>(
  value: T,
  path: readonly [K1, K2, K3],
  update: (current: T[K1][K2][K3]) => T[K1][K2][K3]
): T
export function updateIn<
  T,
  K1 extends keyof T,
  K2 extends keyof T[K1],
  K3 extends keyof T[K1][K2],
  K4 extends keyof T[K1][K2][K3]
>(
  value: T,
  path: readonly [K1, K2, K3, K4],
  update: (current: T[K1][K2][K3][K4]) => T[K1][K2][K3][K4]
): T
export function updateIn<
  T,
  K1 extends keyof T,
  K2 extends keyof T[K1],
  K3 extends keyof T[K1][K2],
  K4 extends keyof T[K1][K2][K3],
  K5 extends keyof T[K1][K2][K3][K4]
>(
  value: T,
  path: readonly [K1, K2, K3, K4, K5],
  update: (current: T[K1][K2][K3][K4][K5]) => T[K1][K2][K3][K4][K5]
): T
export function updateIn(
  value: unknown,
  path: readonly PropertyKey[],
  update: (current: never) => unknown
): unknown {
  return updateAt(value, path, update as (current: unknown) => unknown)
}

/** `updateIn` with a value in place of the update. */
export function putIn<T, K1 extends keyof T>(value: T, path: readonly [K1], next: T[K1]): T
export function putIn<T, K1 extends keyof T, K2 extends keyof T[K1]>(
  value: T,
  path: readonly [K1, K2],
  next: T[K1][K2]
): T
export function putIn<T, K1 extends keyof T, K2 extends keyof T[K1], K3 extends keyof T[K1][K2]>(
  value: T,
  path: readonly [K1, K2, K3],
  next: T[K1][K2][K3]
): T
export function putIn<
  T,
  K1 extends keyof T,
  K2 extends keyof T[K1],
  K3 extends keyof T[K1][K2],
  K4 extends keyof T[K1][K2][K3]
>(value: T, path: readonly [K1, K2, K3, K4], next: T[K1][K2][K3][K4]): T
export function putIn<
  T,
  K1 extends keyof T,
  K2 extends keyof T[K1],
  K3 extends keyof T[K1][K2],
  K4 extends keyof T[K1][K2][K3],
  K5 extends keyof T[K1][K2][K3][K4]
>(value: T, path: readonly [K1, K2, K3, K4, K5], next: T[K1][K2][K3][K4][K5]): T
export function putIn(value: unknown, path: readonly PropertyKey[], next: unknown): unknown {
  return updateAt(value, path, () => next)
}

function updateAt(
  value: unknown,
  [key, ...rest]: readonly PropertyKey[],
  update: (current: unknown) => unknown
): unknown {
  const container = value as Record<PropertyKey, unknown>
  const next = rest.length == 0 ? update(container[key]) : updateAt(container[key], rest, update)
  if (Array.isArray(value)) {
    const copy = value.slice()
    copy[key as number] = next
    return copy
  }
  return { ...container, [key]: next }
}
